import { Breadcrumbs } from '@frontend/breadcrumbs';
import { Squircle } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { User } from '@frontend/user/utils';
import React from 'react';
import { LuLanguages, LuSettings } from 'react-icons/lu';

import AccountMembership from './account-membership/account-membership.component';
import GlobalSearch from './global-search/global-search.component';
import useHeader from './header-controller';
import LanguageSelectModal from './language-select-modal/language-select-modal.component';

/** Telloport md (8px) — one step below lg; still G2 via Squircle. */
const HEADER_ACTION_CORNER_RADIUS_PX = 8;

export interface Props {
    className?: string;
}

export const Header = (props: Props) => {
    const { className, ...rest } = props;
    const viewProps = useHeader(rest);

    return (
        <div className={'top-0 ' + (className ?? '')}>
            <header
                className='bg-background-surface border-border-default sticky top-0 z-50 flex h-18 w-full items-center gap-4 border-b'
                id='main-platform-header'>
                {viewProps.adminEnabled && (
                    <div className='mx-6 flex items-center'>
                        <GlobalSearch />
                    </div>
                )}
                <div className='flex h-full w-full items-center justify-end'>
                    <div className='inline-flex items-center justify-end gap-2 px-5'>
                        {viewProps.adminEnabled && (
                            <ActionButton
                                id='admin-button'
                                icon={<LuSettings className='size-5 w-full' />}
                                onClick={() => viewProps.navigate('/admin')}
                            />
                        )}
                        <ActionButton
                            id='language-button'
                            icon={<LuLanguages className='size-5 w-full' />}
                            onClick={() => viewProps.setShowLanguageSelect(true)}
                        />
                    </div>
                    <a
                        data-testid='profile-link'
                        className='border-border-default hover:bg-brand-light flex h-full cursor-pointer items-center gap-3 border-l px-5'
                        href='/profile'
                        onClick={(e) => {
                            e.preventDefault();
                            viewProps.navigate('/profile');
                        }}>
                        {viewProps.user && (
                            <div className='bg-brand-structure text-background-surface flex size-10 items-center justify-center rounded-full text-lg font-medium'>
                                {new User(viewProps.user).getInitials()}
                            </div>
                        )}
                        <div className='flex flex-col'>
                            <span className='text-foreground-default text-base font-semibold'>
                                {viewProps.user ? new User(viewProps.user).getDisplayName() : CommonMessage.OBJECTS.USER.DEFAULT}
                            </span>
                            <span className='text-brand-muted text-xs font-medium'>{viewProps.user?.unique_identifier}</span>
                        </div>
                    </a>

                    <AccountMembership buttonStyle='border-border-default text-brand-muted flex items-center h-full border-l px-5 cursor-pointer uppercase font-extrabold hover:bg-brand-light' />
                </div>
            </header>
            {viewProps.showBreadcrumbs && (
                <div
                    id='main-platform-breadcrumbs'
                    className='bg-background-default inline-flex w-full items-center justify-start gap-2 self-stretch px-6 pt-3'>
                    <Breadcrumbs />
                </div>
            )}

            {viewProps.showLanguageSelect && <LanguageSelectModal onClose={() => viewProps.setShowLanguageSelect(false)} />}
        </div>
    );
};
export default Header;

const searchBarIcon = () => {
    return (
        <div className='outline-brand-muted inline-flex size-5 flex-col items-center justify-center rounded-sm outline-1 outline-offset-[-1px]'>
            <div className='text-brand-muted justify-start text-sm font-medium'>/</div>
        </div>
    );
};

interface ActionButtonProps {
    icon: React.ReactNode;
    hidden?: boolean;
    onClick?: () => void;
    id?: string;
}

const ActionButton = (props: ActionButtonProps) => {
    if (props.hidden) {
        return <></>;
    }
    return (
        <Squircle
            data-testid={props.id}
            onClick={() => props.onClick && props.onClick()}
            data-variant='Default'
            cornerRadius={HEADER_ACTION_CORNER_RADIUS_PX}
            className='bg-background-default text-foreground-default hover:bg-brand-light flex size-9 items-center justify-center hover:cursor-pointer'>
            {props.icon}
        </Squircle>
    );
};
